import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star, Check, X, Loader2, Trash2, Filter, Plus, MessageSquareQuote } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../../lib/api";

const STATUSES = [
  { key: "pending", label: "Pending" },
  { key: "approved", label: "Approved (live)" },
  { key: "rejected", label: "Rejected" },
];

const Stars = ({ n }) => (
  <span className="inline-flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} size={14} className={i <= n ? "fill-[#f28a16] text-[#f28a16]" : "text-[#cfd7db]"} />
    ))}
  </span>
);

export const AdminTestimonials = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("pending");
  const [busyId, setBusyId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ rating: 5, comment: "", author_name: "", permission_to_publish: true });

  const load = async () => {
    setLoading(true);
    try { setRows(await adminApi.listTestimonials(status)); }
    catch { toast.error("Could not load testimonials"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const doApprove = async (id) => {
    setBusyId(id);
    try { await adminApi.approveTestimonial(id, "Admin"); toast.success("Approved — now live"); load(); }
    catch { toast.error("Could not approve"); } finally { setBusyId(null); }
  };
  const doReject = async (id) => {
    setBusyId(id);
    try { await adminApi.rejectTestimonial(id); toast.success("Rejected"); load(); }
    catch { toast.error("Could not reject"); } finally { setBusyId(null); }
  };
  const doDelete = async (id) => {
    if (!window.confirm("Delete this testimonial permanently?")) return;
    setBusyId(id);
    try { await adminApi.deleteTestimonial(id); toast.success("Deleted"); load(); }
    catch { toast.error("Could not delete"); } finally { setBusyId(null); }
  };

  const submitManual = async (e) => {
    e.preventDefault();
    if (!form.author_name.trim()) return toast.error("Author name is required");
    try {
      await adminApi.createTestimonial({ ...form, rating: Number(form.rating) });
      toast.success("Testimonial captured — awaiting approval");
      setShowForm(false);
      setForm({ rating: 5, comment: "", author_name: "", permission_to_publish: true });
      setStatus("pending");
      load();
    } catch { toast.error("Could not save"); }
  };

  const counts = { pending: rows.length };

  return (
    <div data-testid="admin-testimonials-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display font-black uppercase text-3xl sm:text-4xl text-[#111619]">Testimonials</h1>
          <p className="text-[#3f4b52] mt-1">
            Reviews collected at pre-fitment sign-off (with client permission). Approve to publish to the public site.
          </p>
        </div>
        <button
          onClick={() => setShowForm((v) => !v)}
          data-testid="testimonial-manual-btn"
          className="bg-[#f28a16] text-white px-4 h-10 rounded-md font-display uppercase tracking-widest text-xs inline-flex items-center gap-2"
        >
          <Plus size={14} /> Capture manually
        </button>
      </div>

      {showForm && (
        <form onSubmit={submitManual} className="mt-6 bg-white border border-[#cfd7db] rounded-md p-5 space-y-3 max-w-2xl" data-testid="testimonial-manual-form">
          <div className="font-display uppercase tracking-widest text-xs text-[#f28a16]">Manual capture</div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase font-display tracking-widest text-[#3f4b52]">Rating</label>
              <select value={form.rating} onChange={(e) => setForm({ ...form, rating: e.target.value })} className="mt-1 w-full h-10 px-3 border border-[#cfd7db] rounded-md text-sm">
                <option value={5}>5 stars</option>
                <option value={4}>4 stars</option>
                <option value={3}>3 stars</option>
                <option value={2}>2 stars</option>
                <option value={1}>1 star</option>
              </select>
            </div>
            <div>
              <label className="text-xs uppercase font-display tracking-widest text-[#3f4b52]">Author name</label>
              <input value={form.author_name} onChange={(e) => setForm({ ...form, author_name: e.target.value })} className="mt-1 w-full h-10 px-3 border border-[#cfd7db] rounded-md text-sm" />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase font-display tracking-widest text-[#3f4b52]">Comment</label>
            <textarea value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} rows={3} className="mt-1 w-full px-3 py-2 border border-[#cfd7db] rounded-md text-sm" />
          </div>
          <label className="flex items-center gap-2 text-sm text-[#3f4b52]">
            <input type="checkbox" checked={form.permission_to_publish} onChange={(e) => setForm({ ...form, permission_to_publish: e.target.checked })} className="h-4 w-4 accent-[#f28a16]" />
            Author has given permission to publish
          </label>
          <button type="submit" className="bg-[#20282c] text-white px-4 h-10 rounded-md font-display uppercase tracking-widest text-xs">Save</button>
        </form>
      )}

      {/* Filter */}
      <div className="mt-6 flex items-center gap-2 flex-wrap">
        <Filter size={14} className="text-[#3f4b52]" />
        {STATUSES.map((s) => (
          <button
            key={s.key}
            onClick={() => setStatus(s.key)}
            data-testid={`testimonials-tab-${s.key}`}
            className={`px-3 h-9 rounded-md font-display uppercase tracking-widest text-[11px] ${status === s.key ? "bg-[#111619] text-white" : "bg-white border border-[#cfd7db] text-[#3f4b52] hover:border-[#f28a16]"}`}
          >
            {s.label}
          </button>
        ))}
      </div>

      <div className="mt-4 bg-white border border-[#cfd7db] rounded-md overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-[#3f4b52] inline-flex items-center gap-2 justify-center w-full">
            <Loader2 className="animate-spin" size={16} /> Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center">
            <MessageSquareQuote size={30} className="mx-auto text-[#9aa6ad]" />
            <p className="mt-2 text-sm text-[#3f4b52]">No {status} testimonials.</p>
            <Link to="/admin/customers" className="text-xs text-[#f28a16] hover:underline mt-1 inline-block">
              Share a pre-fitment link with a customer →
            </Link>
          </div>
        ) : (
          <ul className="divide-y divide-[#eef2f4]" data-testid="testimonial-list">
            {rows.map((t) => (
              <li key={t.id} className="p-5 flex items-start gap-4 flex-wrap" data-testid={`testimonial-row-${t.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Stars n={t.rating} />
                    <span className="font-display font-bold text-[#111619]">{t.author_name || "Anonymous"}</span>
                    {t.customer_code && <span className="font-mono text-xs text-[#f28a16]">· {t.customer_code}</span>}
                    <span className="text-xs font-display uppercase tracking-widest text-[#3f4b52] bg-[#edf2f4] px-2 py-0.5 rounded-sm">
                      {t.source === "prefitment" ? "From sign-off" : t.source === "manual" ? "Manual capture" : "Direct"}
                    </span>
                  </div>
                  {t.comment && <p className="mt-2 text-sm text-[#3f4b52] italic">&ldquo;{t.comment}&rdquo;</p>}
                  <div className="text-xs text-[#9aa6ad] mt-1">
                    Submitted {new Date(t.created_at).toLocaleString("en-ZA")}
                    {t.approved_at && <> · Approved {new Date(t.approved_at).toLocaleString("en-ZA")}</>}
                    {!t.permission_to_publish && <span className="text-[#dc2626] ml-2">· No publish permission</span>}
                  </div>
                </div>
                <div className="flex gap-2">
                  {t.status !== "approved" && (
                    <button
                      onClick={() => doApprove(t.id)}
                      disabled={busyId === t.id || !t.permission_to_publish}
                      data-testid={`testimonial-approve-${t.id}`}
                      className="bg-[#10b981] text-white px-3 h-9 rounded-md font-display uppercase tracking-widest text-[11px] inline-flex items-center gap-1 disabled:opacity-40"
                    >
                      <Check size={12} /> Approve
                    </button>
                  )}
                  {t.status !== "rejected" && (
                    <button
                      onClick={() => doReject(t.id)}
                      disabled={busyId === t.id}
                      className="border border-[#cfd7db] text-[#3f4b52] px-3 h-9 rounded-md font-display uppercase tracking-widest text-[11px] inline-flex items-center gap-1 disabled:opacity-40 hover:bg-[#edf2f4]"
                    >
                      <X size={12} /> Reject
                    </button>
                  )}
                  <button
                    onClick={() => doDelete(t.id)}
                    disabled={busyId === t.id}
                    className="text-[#dc2626] hover:bg-[#dc2626]/10 h-9 w-9 rounded-md inline-flex items-center justify-center disabled:opacity-40"
                    aria-label="Delete"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default AdminTestimonials;
